import { Button } from '@simple-agent-manager/ui';
import { type ReactNode, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';

import { useScrollLock } from '../hooks/useScrollLock';

interface ConfirmDialogProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => void;
  title: string;
  message: ReactNode;
  confirmLabel?: string;
  cancelLabel?: string;
  variant?: 'danger' | 'warning' | 'info';
  loading?: boolean;
}

const variantConfig = {
  danger: {
    iconColorClass: 'text-danger',
    iconBgClass: 'bg-danger-tint',
    iconPath: 'M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z',
  },
  warning: {
    iconColorClass: 'text-warning',
    iconBgClass: 'bg-warning-tint',
    iconPath: 'M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z',
  },
  info: {
    iconColorClass: 'text-accent',
    iconBgClass: 'bg-accent-tint',
    iconPath: 'M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z',
  },
};

/**
 * Confirmation dialog component.
 */
export function ConfirmDialog({
  isOpen,
  onClose,
  onConfirm,
  title,
  message,
  confirmLabel = 'Confirm',
  cancelLabel = 'Cancel',
  variant = 'danger',
  loading = false,
}: ConfirmDialogProps) {
  const dialogRef = useRef<HTMLDivElement>(null);

  // Handle escape key
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isOpen && !loading) {
        onClose();
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, loading, onClose]);

  // Prevent body scroll when open
  useScrollLock(isOpen);

  // Focus trap
  useEffect(() => {
    if (isOpen && dialogRef.current) {
      dialogRef.current.focus();
    }
  }, [isOpen]);

  if (!isOpen) return null;

  const config = variantConfig[variant];

  return createPortal(
    <div
      className="fixed inset-0 z-dialog-backdrop overflow-y-auto"
      aria-labelledby="modal-title"
      role="dialog"
      aria-modal="true"
    >
      {/* Backdrop */}
      <div
        className="fixed inset-0 glass-backdrop-dim transition-opacity duration-150"
        onClick={loading ? undefined : onClose}
      />

      <div className="flex min-h-full items-center justify-center p-4">
        <div
          ref={dialogRef}
          tabIndex={-1}
          className="relative glass-modal glass-panel-container glass-composited rounded-lg shadow-overlay max-w-md w-full p-6 outline-none"
        >
          <div className="flex items-start">
            <div className={`shrink-0 flex items-center justify-center h-12 w-12 rounded-full ${config.iconBgClass}`}>
              <svg className={`h-6 w-6 ${config.iconColorClass}`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d={config.iconPath} />
              </svg>
            </div>
            <div className="ml-4 flex-1">
              <h3 className="text-base font-semibold text-fg-primary" id="modal-title">
                {title}
              </h3>
              <div className="mt-2 text-sm text-fg-muted">{message}</div>
            </div>
          </div>

          <div className="mt-6 flex justify-end gap-3">
            <Button
              variant="secondary"
              disabled={loading}
              onClick={onClose}
            >
              {cancelLabel}
            </Button>
            <Button
              variant={variant === 'danger' ? 'danger' : 'primary'}
              disabled={loading}
              onClick={onConfirm}
              loading={loading}
            >
              {confirmLabel}
            </Button>
          </div>
        </div>
      </div>
    </div>,
    document.body,
  );
}
